import {
  faArrowRight,
  faTag,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import useIsMobile from "../../Hook/mobile";
import { displayPopin } from "../../Hook/popins";
import useUser from "../../Hook/user";

function OfferCodePrompt() {
  const user = useUser();
  const isMobile = useIsMobile();
  const display = displayPopin();

  const offer = user?.offerCode;
  const code = offer?.code?.trim();
  const isExpired =
    typeof offer?.time_expiration === "number" &&
    offer.time_expiration > 0 &&
    offer.time_expiration < Date.now() / 1000;
  const storageKey = useMemo(
    () => (code ? `offercode:dismissed:${code}` : ""),
    [code],
  );
  const [dismissed, setDismissed] = useState(() => {
    if (!storageKey) return false;
    return localStorage.getItem(storageKey) === "1";
  });

  useEffect(() => {
    if (!storageKey) {
      setDismissed(false);
      return;
    }
    setDismissed(localStorage.getItem(storageKey) === "1");
  }, [storageKey]);

  if (!user?.informations) return null;
  if (user.informations.type === "guest") return null;
  if (!code || isExpired || dismissed) return null;

  const close = () => {
    localStorage.setItem(storageKey, "1");
    setDismissed(true);
  };

  const displaySub = () => {
    display("subscribe", { code });
    localStorage.setItem(storageKey, "1");
    setDismissed(true);
  };

  return (
    <div
      className={
        "fixed z-[45] " +
        (isMobile.isMobile
          ? "left-1/2 -translate-x-1/2 bottom-24"
          : "right-24 bottom-24")
      }
    >
      <div
        onClick={displaySub}
        className={
          "group relative overflow-hidden cursor-pointer border border-[#9dc6ff55] bg-gradient-to-br from-[#4f9cff] via-[#2d6ceb] to-[#1f46be] text-white shadow-[0_18px_40px_rgba(20,56,163,0.38)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_54px_rgba(20,56,163,0.45)] rounded-[22px] w-[308px]"
        }
      >
        <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/20 blur-2xl" />
        <div className="pointer-events-none absolute inset-0 opacity-35 bg-[radial-gradient(circle_at_18%_12%,rgba(255,255,255,0.38),transparent_38%)]" />
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            close();
          }}
          className="absolute right-2 z-[100] top-2 h-7 w-7 rounded-full text-white/85 transition-colors hover:bg-white/15 hover:text-white"
          aria-label="close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="relative z-[1] flex flex-col gap-3 px-4 py-4 pr-11">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#d6e9ff]">
              <FormattedMessage
                id="offer_code_prompt__kicker"
                defaultMessage="Limited promo"
              />
            </p>
            <p className="mt-1 text-[29px] font-black leading-[1.02] tracking-[-0.02em]">
              {!!offer?.pc_discount ? `-${offer.pc_discount}%` : "Promo"}
            </p>
            <p className="mt-1 text-[24px] font-bold leading-[1.05] tracking-[-0.02em]">
              <FormattedMessage
                id="offer_code_prompt__title"
                defaultMessage="Subscription discount"
              />
            </p>
            <p className="mt-1 text-[12px] text-white/80">
              <FormattedMessage
                id="offer_code_prompt__subtitle"
                defaultMessage="Tap to apply this offer automatically"
              />
            </p>
            <div className="mt-3 flex items-center gap-2">
              <span className="inline-flex rounded-full border border-white/40 bg-white/18 px-3 py-1 text-[12px] font-bold tracking-[0.08em]">
                {code}
              </span>
            </div>
          </div>

          <div className="absolute right-3 bottom-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#1f46be] shadow-md">
            <FontAwesomeIcon
              icon={faArrowRight}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default OfferCodePrompt;
